/* Tolvora core: window.TV (docs/design-system.md §4). Loaded on every page, in both site modes: events,
   money, drawers (open, close, focus, links), cart count, waitlist form and its header button. Nothing here
   needs tv-cart.js. */
(() => {
  const d = document;
  const TV = (window.TV = window.TV || {});
  const all = (selector) => d.querySelectorAll(selector);

  TV.on = (name, fn, capture) => d.addEventListener(name, fn, capture);
  TV.emit = (name, detail) => d.dispatchEvent(new CustomEvent(name, { detail }));

  // Shopify money formats ({{amount}}, {{amount_with_comma_separator}}…)
  TV.money = (cents, format) =>
    (format || window.TV_CONFIG?.moneyFormat || '${{amount}}').replace(/{{\s*(\w+)\s*}}/, (m, key) => {
      const has = (word) => key.includes(word);
      const [sep, point] = has('comma') ? '.,' : has('apos') ? "'." : has('period') ? ' .' : has('space') ? ' ,' : ',.';
      const [int, frac] = (Math.round(+cents || 0) / 100).toFixed(has('no_dec') ? 0 : 2).split('.');
      return int.replace(/\B(?=(\d{3})+$)/g, sep) + (frac ? point + frac : '');
    });

  // Drawers: dialog.tv-drawer#id, [data-tv-open="id"], [data-tv-close]
  const find = (id) => {
    const el = typeof id == 'string' ? d.getElementById(id) : id;
    return el?.showModal && el.matches('.tv-drawer') ? el : null;
  };
  const sync = (el) => all(`[data-tv-open="${el.id}"]`).forEach((t) => t.setAttribute('aria-expanded', el.open));

  TV.drawer = {
    open(id, trigger = d.activeElement) {
      const el = find(id);
      if (!el || el.open) return !!el;
      const parent = trigger?.closest?.('.tv-drawer[open]');
      if (parent) {
        trigger = parent.tvTrigger;
        parent.close();
      }
      el.tvTrigger = trigger;
      el.showModal();
      sync(el);
      return true;
    },
    close(id) {
      const el = find(id);
      if (el) el.close();
    },
  };

  TV.on('click', (e) => {
    const t = e.target;
    if (!t.closest) return;
    const opener = t.closest('[data-tv-open]');
    const el = t.closest('.tv-drawer');
    if (opener) {
      if (opener.href && (e.metaKey || e.ctrlKey || e.shiftKey)) return;
      if (TV.drawer.open(opener.dataset.tvOpen, opener)) e.preventDefault();
    } else if (el && t.closest('[data-tv-close]')) {
      e.preventDefault();
      el.close();
    } else if (t == el) {
      // Backdrop click: targets the dialog, outside its box
      const r = el.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) el.close();
    }
  });

  // A link followed inside a drawer closes it: the menu links are anchors of the current page. The browser
  // then starts keyboard navigation from the link's target (#faq…): without a trigger, the "close" listener
  // below does not pull the focus back to "Open menu". On window: runs after every listener on document, so
  // a click that another script cancelled is left alone.
  addEventListener('click', (e) => {
    const el = e.target.closest?.('.tv-drawer[open] a[href]')?.closest('.tv-drawer');
    if (!el || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey) return;
    el.tvTrigger = 0;
    el.close();
  });

  // "close" does not bubble (capture); also fires on Escape. The focus returns to the trigger. A trigger of
  // the sticky header (menu, cart) is always on screen, but inside the scroll-padding of <html> (tv-base.css):
  // focused by script, it would make the browser scroll the page up to "reveal" it, hence preventScroll.
  TV.on('close', (e) => {
    const el = find(e.target);
    if (!el) return;
    sync(el);
    const t = el.tvTrigger;
    el.tvTrigger = 0;
    if (t?.isConnected && !d.querySelector('dialog[open]')) t.focus({ preventScroll: !!t.closest('.tv-header-section') });
  }, true);

  TV.on('tv:cart:updated', (e) => {
    const n = e.detail?.cart?.item_count;
    if (n != null) all('[data-tv-cart-count]').forEach((el) => ((el.textContent = n), (el.hidden = !n)));
  });

  // Waitlist (snippets/tv-waitlist-form). A submission that does not leave: the form says why in its
  // [data-tv-waitlist-hint] alert, in the language of the page. key: email, consent or retry; none: hide it.
  const hint = (form, key) => {
    const el = form?.querySelector?.('[data-tv-waitlist-hint]');
    if (!el) return;
    el.hidden = !key;
    el.textContent = key ? el.dataset[key] : '';
  };
  // Refused by the browser. "invalid" does not bubble (capture) and fires once per invalid field: only the
  // first one of the form speaks, the one the browser focuses.
  TV.on('invalid', (e) => {
    const t = e.target;
    if (t.form?.querySelector(':invalid') == t) hint(t.form, t.matches('[data-tv-waitlist-consent]') ? 'consent' : 'email');
  }, true);
  // Hidden as soon as the visitor types or ticks. Not on "change": for the email field it fires when the
  // field loses the focus, that is under a press on the button, which would move away before the click.
  TV.on('input', (e) => hint(e.target.form));
  // A filled honeypot cancels the submission. The field is emptied and the form says so: a person whose
  // autofill wrote in the field can press the button again.
  TV.on('submit', (e) => {
    const hp = e.target.querySelector?.('[data-tv-waitlist-hp]');
    if (!hp?.value) return;
    e.preventDefault();
    hp.value = '';
    hint(e.target, 'retry');
  });

  // A waitlist link to an anchor of this very page (header button, "Join the waitlist" links): the focus goes
  // to the email field of the form found there. In a timer: the browser clears the focus when it jumps to
  // the anchor, right after the click. The browser may scroll: a field left off screen by the jump (zoomed
  // page, short window) is brought into view, below the header (scroll-padding of <html>, tv-base.css).
  TV.on('click', (e) => {
    const a = e.target.closest?.('a[data-tv-waitlist-cta],a[data-tv-waitlist-link]');
    if (!a?.hash || a.href.split('#')[0] != location.href.split('#')[0] || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const field = d.getElementById(a.hash.slice(1))?.querySelector('[data-tv-waitlist-email]');
    if (field) setTimeout(() => field.focus());
  });

  // Dated texts (D76, the launch price): an element with data-tv-expires (an ISO 8601 time) is removed once that
  // time has passed, or if it cannot be read. Liquid already leaves it out after that time; this covers a page
  // served from a cache, or left open past it. tv-cart.js calls TV.expire() after it renders the drawer.
  TV.expire = () => {
    let next = Infinity;
    all('[data-tv-expires]').forEach((el) => {
      const left = Date.parse(el.dataset.tvExpires) - Date.now();
      if (left > 0) next = Math.min(next, left);
      else el.remove();
    });
    // A deadline more than a day away: check again in a day (a page left open for days still loses the text).
    clearTimeout(TV.expire.timer);
    if (next < Infinity) TV.expire.timer = setTimeout(TV.expire, Math.min(next + 1000, 864e5));
  };
  TV.expire();

  // Loaded with defer: the document is parsed
  all('.tv-drawer[id]').forEach(sync);

  // Header waitlist button (sections/tv-header): one filled button per screen. Outlined while a submit button
  // of a waitlist form is on screen, below the sticky header; filled again when none is.
  const cta = d.querySelector('a[data-tv-waitlist-cta]');
  const submits = all('[data-tv-waitlist-submit]');
  // Signed up: no button (the stylesheet says it with :has(); this is for a browser without it)
  if (cta && d.querySelector('[data-tv-waitlist-success]')) cta.hidden = true;
  if (cta && submits.length && window.IntersectionObserver) {
    const seen = new Set();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => seen[entry.isIntersecting ? 'add' : 'delete'](entry.target));
        cta.classList.toggle('tv-header__cta--quiet', seen.size > 0);
      },
      { rootMargin: `-${cta.closest('.tv-header')?.offsetHeight || 0}px 0px 0px` }
    );
    submits.forEach((el) => io.observe(el));
  }

  // Back from Shopify: focus goes to the confirmation, or to the error, of the form named by the hash.
  // No hash, or a hash that names nothing: to the first one displayed in the page.
  // On load: before that, the browser clears the focus when it scrolls to the anchor.
  addEventListener('load', () => {
    const result = '[data-tv-waitlist-success],[data-tv-waitlist-error]:not([hidden])';
    const at = d.getElementById(location.hash.slice(1));
    (at ? at.querySelector(result) : d.querySelector(result))?.focus();
  });
})();
